/**
 * DcaLibrary — Desk+ multi-exchange DCA strategy templates.
 *
 * Templates deploy as *your* agent/strategy configs (WORK-PLAN C2/C3).
 * Deploy uses DeployAsAgentButton (L4): Desk+ navigates to /ai-trading?template=
 * or opens configure stub; Free/Starter cannot complete deploy.
 * Not a signal marketplace, not copy trading, no fake returns.
 *
 * Craft floor: ivory modules / champagne accents via DeskModule parent
 * or standalone list; --color-* tokens only.
 */

'use client'

import React from 'react'
import Link from 'next/link'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import { DeployAsAgentButton } from './DeployAsAgentButton'
import {
  DCA_TEMPLATES,
  type DcaTemplate,
} from './dca-templates'
import styles from './DcaLibrary.module.css'

export interface DcaLibraryProps {
  /** Override catalog (defaults to honest DCA_TEMPLATES stubs). */
  templates?: readonly DcaTemplate[]
  /**
   * Optional hook when Pro navigation to the agent desk is about to run.
   * Does not imply a live agent was created or any returns.
   */
  onNavigate?: (href: string) => void
  /** Optional class on the root. */
  className?: string
  /** Hide the short intro lead (when parent DeskShell already explains). */
  hideIntro?: boolean
  /**
   * Deploy control mode:
   * - navigate (default): /ai-trading?template=…&kind=dca&intent=deploy
   * - panel: honest configure stub then continue to agent desk
   */
  deployMode?: 'navigate' | 'panel'
}

export function DcaLibrary({
  templates = DCA_TEMPLATES,
  onNavigate,
  className = '',
  hideIntro = false,
  deployMode = 'navigate',
}: DcaLibraryProps) {
  const { canUseGridDca, isLoading: entitlementsLoading, isFree, tier } =
    usePlanEntitlements()

  return (
    <div
      className={`${styles.root} ${className}`.trim()}
      data-surface="dca-library"
      data-entitled={canUseGridDca ? 'true' : 'false'}
    >
      {!hideIntro ? (
        <p className={styles.intro}>
          DCA templates are deployable strategy configs for agents you supervise.
          No copy trading, no stranger signals, no advertised returns — only schedule,
          size style, and exchange connectors you configure.
        </p>
      ) : null}

      {!entitlementsLoading && !canUseGridDca ? (
        <p className={styles.planNote} role="status">
          Multi-exchange DCA library is on Desk ($99.99/mo) and above.
          {isFree || tier === 'starter' || tier === 'none' ? (
            <>
              {' '}
              <Link className={styles.planLink} href="/settings/billing">
                Start free
              </Link>
            </>
          ) : null}
        </p>
      ) : null}

      <ul className={styles.grid} aria-label="DCA strategy templates">
        {templates.map((template) => (
          <li key={template.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.kindChip}>DCA · agent config</span>
              <h3 className={styles.name}>{template.name}</h3>
            </div>

            <p className={styles.description}>{template.description}</p>

            <div className={styles.meta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Exchanges</span>
                <ul className={styles.exchangeList} aria-label="Exchange placeholders">
                  {template.exchanges.map((ex) => (
                    <li key={ex} className={styles.exchangeChip}>
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Interval</span>
                <span className={styles.metaValue}>{template.defaults.interval}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Size</span>
                <span className={styles.metaValue}>{template.defaults.sizeStyle}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Orders</span>
                <span className={styles.metaValue}>{template.defaults.orderStyle}</span>
              </div>
            </div>

            {template.notes ? (
              <p className={styles.notes}>{template.notes}</p>
            ) : null}

            <div className={styles.footer}>
              <DeployAsAgentButton
                templateId={template.id}
                templateName={template.name}
                kind="dca"
                mode={deployMode}
                compact
                label="Deploy as agent"
                onNavigate={onNavigate}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default DcaLibrary
